import { CommercialClientTermResponse } from '@interfaces/partners/responses/commercial-client-term.response';

/**
 * Abas da tela do deal. O Parecer é card próprio. `calls` (Ligações, card 3112) é um painel próprio,
 * `app-deal-meetings-panel`, visível para todos que veem o deal.
 *
 * `quotes` (Cotação, card 3611, 20/10/2026) existe em todas as etapas, para o time interno e para o
 * parceiro: os empréstimos com status Cotado da empresa do deal (`app-deal-quotes-panel`). É a última aba,
 * depois de Linha do tempo (decisão do Marcio em 21/10/2026).
 *
 * `documents` existe em todas as etapas do deal (card 3239, 02/10/2026). Desde o card 3281 (T04) abre
 * no Data Room da empresa (`app-deal-data-room-tab`), com o onboarding embutido a um clique.
 *
 * `banks` (Bancos, card 3707) é condicional: aparece quando o deal tem negociação do pipe de crédito ligada, em
 * qualquer etapa (`DealBankJourneyStore.unlocked`), para o time interno e para o parceiro. Desde o card 3752 não é
 * mais um botão entre as abas: é a visão **Jornada nos bancos** do seletor do cabeçalho (`app-deal-journey-switch`),
 * que troca o stepper pelo resumo dos bancos e esconde as abas do deal. O valor continua `banks` na URL, então link
 * antigo com `?tab=banks` abre direto nessa visão.
 *
 * `economic-analysis` é condicional (card 3268). Quem decide o que renderizar é o
 * {@link resolveShownTab}, não a aba selecionada: a aba pode sumir debaixo de uma seleção viva.
 *
 * Os valores também são os da URL (`?tab=`, card 3307): mudar um deles quebra link já compartilhado.
 */
export type DealTab =
  | 'overview'
  | 'banks'
  | 'quotes'
  | 'calls'
  | 'documents'
  | 'economic-analysis'
  | 'kyc'
  | 'timeline';

export const DEAL_TABS: readonly DealTab[] = [
  'overview',
  'banks',
  'calls',
  'documents',
  'economic-analysis',
  'kyc',
  'timeline',
  'quotes',
];

/**
 * Se a aba **Análise econômica** aparece.
 *
 * Desde o card 3307 (T02/T03, 07/10/2026) em **todas as etapas**, para o time interno **e** para o
 * parceiro, sempre que houver dados: `hasEconomicAnalysis` vem do backend e diz se a empresa do deal
 * tem pelo menos um exercício na DFP Histórica (o mesmo critério que as tabelas usam). Até ali a aba
 * existia só na etapa Gates e só para o time interno (card 3268).
 *
 * - Time interno: exige `DFP_VIEW`, a permissão da rota da Análise Econômica; sem ela a tela embutida
 *   renderizaria vazia.
 * - Parceiro: não tem nem ganha `DFP_VIEW`. Quem autoriza é o endpoint do deal
 *   (`partners/deals/{id}/economic-analysis`), que confere que o deal é dele.
 */
export function canSeeEconomicAnalysis(
  hasEconomicAnalysis: boolean,
  isInternalView: boolean,
  hasDfpPermission: boolean
): boolean {
  return hasEconomicAnalysis && (!isInternalView || hasDfpPermission);
}

/**
 * A aba realmente exibida.
 *
 * A Análise econômica pode sumir com ela selecionada: o deal sai de Gates (uma decisão o move) ou a
 * pessoa escolhe outra etapa no stepper. Sem este desvio, a tela ficaria em branco com uma aba
 * selecionada que não está mais na lista.
 */
export function resolveShownTab(
  tab: DealTab,
  visible: { economicAnalysis: boolean; kyc?: boolean; banks?: boolean }
): DealTab {
  if (tab === 'economic-analysis' && !visible.economicAnalysis) return 'overview';
  if (tab === 'kyc' && !visible.kyc) return 'overview';
  if (tab === 'banks' && !visible.banks) return 'overview';
  return tab;
}

/** O termo NDA + SCR do cliente, quando ele está **assinado**; `null` em qualquer outro estado. */
export function signedClientTerm(
  term: CommercialClientTermResponse | null | undefined
): CommercialClientTermResponse | null {
  return term?.status === 'Assinado' && term.signedAt ? term : null;
}

/**
 * Se a aba **KYC** aparece (card 3380).
 *
 * Em **todas as etapas**, **só para o time interno** e **só quando há o que mostrar**: o cadastro da
 * Receita (com ou sem sócios) ou o termo do cliente assinado. Aba sem dado não aparece, nem vazia.
 * O parceiro nunca vê: o KYC é leitura interna da Legatus sobre o cliente dele.
 */
export function canSeeKyc(
  isInternalView: boolean,
  hasRegistry: boolean,
  term: CommercialClientTermResponse | null | undefined
): boolean {
  return isInternalView && (hasRegistry || signedClientTerm(term) !== null);
}
